import { defineStore } from 'pinia';
import { Socket } from 'socket.io-client';
import { computed } from 'vue';
import { useSocket } from '../composables/useSocket';
import * as Sentry from '@sentry/vue';

// Get socket singleton once outside the store
let socketInstance: ReturnType<typeof useSocket> | null = null;

function getSocketSingleton() {
    if (!socketInstance) {
        try {
            socketInstance = useSocket();
            
            // Add error handling to the socket instance
            if (socketInstance.socket) {
                socketInstance.socket.on('connect_error', (error) => {
                    Sentry.captureException(error, {
                        tags: { component: 'socketStore', type: 'connection_error' },
                        extra: { message: error.message, type: error.type }
                    });
                });
                
                socketInstance.socket.on('reconnect_failed', () => {
                    Sentry.captureMessage('Socket reconnection failed', {
                        level: 'error',
                        tags: { component: 'socketStore', type: 'reconnect_failed' }
                    });
                });
            }
        } catch (error) {
            Sentry.captureException(error, {
                tags: { component: 'socketStore', phase: 'initialization' },
                level: 'error'
            });
            console.warn('Failed to initialize socket in store context:', error);
            // Return a safe fallback
            return {
                socket: null,
                isConnected: { value: false },
                joinRoom: () => {},
                leaveRoom: () => {}
            };
        }
    }
    return socketInstance;
}

export const useSocketStore = defineStore('socket', () => {
    // Use the singleton version of the socket
    const { socket, isConnected } = getSocketSingleton();

    // Actions with improved error handling
    const joinTrain = (trainId: string) => {
        if (!trainId) {
            const error = new Error('Cannot join train: Missing trainId');
            Sentry.captureException(error, {
                tags: { component: 'socketStore', action: 'joinTrain' },
                level: 'warning'
            });
            console.error(error.message);
            return;
        }
        
        if (socket) {
            try {
                socket.emit('joinTrain', trainId);
                console.info(`Joined train room: train:${trainId}`);
                Sentry.addBreadcrumb({
                    message: `Joined train room: ${trainId}`,
                    category: 'socket',
                    level: 'info'
                });
            } catch (error) {
                Sentry.captureException(error, {
                    tags: { component: 'socketStore', action: 'joinTrain' },
                    extra: { trainId }
                });
                console.error(`Error joining train ${trainId}:`, error);
            }
        } else {
            console.warn('Socket not available when trying to join train:', trainId);
        }
    };

    const leaveTrain = (trainId: string) => {
        if (!trainId) {
            const error = new Error('Cannot leave train: Missing trainId');
            Sentry.captureException(error, {
                tags: { component: 'socketStore', action: 'leaveTrain' },
                level: 'warning'
            });
            console.error(error.message);
            return;
        }
        
        if (socket) {
            try {
                socket.emit('leaveTrain', trainId);
                console.info(`Left train room: train:${trainId}`);
                Sentry.addBreadcrumb({
                    message: `Left train room: ${trainId}`,
                    category: 'socket',
                    level: 'info'
                });
            } catch (error) {
                Sentry.captureException(error, {
                    tags: { component: 'socketStore', action: 'leaveTrain' },
                    extra: { trainId }
                });
                console.error(`Error leaving train ${trainId}:`, error);
            }
        } else {
            console.warn('Socket not available when trying to leave train:', trainId);
        }
    };

    // Computed property to ensure we always return a Socket object
    // This maintains backward compatibility with code that expects socket to never be null
    const safeSocket = computed(() => {
        if (!socket) {
            console.warn('Attempting to access socket before it is initialized');
            // Return a dummy socket that logs warnings instead of throwing errors
            return {
                on: (event: string, callback: any) => {
                    console.warn(`Socket not initialized. Cannot listen to ${event}`);
                    return { socket: null };
                },
                off: (event: string, callback?: any) => {
                    console.warn(`Socket not initialized. Cannot remove listener for ${event}`);
                    return { socket: null };
                },
                emit: (event: string, ...args: any[]) => {
                    console.warn(`Socket not initialized. Cannot emit ${event}`);
                    return { socket: null };
                }
            } as unknown as Socket;
        }
        return socket;
    });

    return {
        socket: safeSocket,
        isConnected,
        joinTrain,
        leaveTrain
    };
});
